import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';

import type { AiCreditsIncentiveItem } from '../../models/ai-credits-incentive.model';
import {
	AiCreditsIncentiveModalData,
	JeptoAiCreditsIncentiveModalComponent,
} from './jepto-ai-credits-incentive-modal.component';

export const AI_CREDITS_INCENTIVE_MODAL_PANEL =
	'ai-credits-incentive-modal-panel';

/** Prefix for dialog ids — each incentive gets `${prefix}-${incentive.id}`. */
export const AI_CREDITS_INCENTIVE_MODAL_ID = 'ai-credits-incentive-modal';

export function aiCreditsIncentiveModalId(
	incentiveId: AiCreditsIncentiveItem['id']
): string {
	return `${AI_CREDITS_INCENTIVE_MODAL_ID}-${incentiveId}`;
}

/** Clears shared store submit error/loading when switching or opening tasks. */
export interface AiCreditsIncentiveSubmitReset {
	clearSubmitError: () => void;
}

export function openAiCreditsIncentiveModal(
	dialog: MatDialog,
	incentive: AiCreditsIncentiveItem,
	submitReset?: AiCreditsIncentiveSubmitReset
): MatDialogRef<JeptoAiCreditsIncentiveModalComponent, boolean> {
	const dialogId = aiCreditsIncentiveModalId(incentive.id);
	const existing = dialog.getDialogById(dialogId) as
		| MatDialogRef<JeptoAiCreditsIncentiveModalComponent, boolean>
		| undefined;
	// Same task already open — reuse so we do not stack identical dialogs.
	if (existing) {
		return existing;
	}

	// closeAll() skips the modal's close() helper, so reset shared submit state
	// here — otherwise the next task inherits error/loading from the previous one.
	submitReset?.clearSubmitError();

	// Close any open MatDialog (other incentive task, account settings, etc.).
	// closeAll() only *starts* the exit animation — the dialog stays in
	// openDialogs until afterClosed. Reusing one shared id would then throw
	// "Dialog with id exists already", so each incentive gets its own id.
	dialog.closeAll();

	const config: MatDialogConfig<AiCreditsIncentiveModalData> = {
		id: dialogId,
		autoFocus: true,
		disableClose: true,
		width: '520px',
		maxWidth: '95vw',
		panelClass: AI_CREDITS_INCENTIVE_MODAL_PANEL,
		// Drop exit animation so the previous task clears faster when switching.
		enterAnimationDuration: 0,
		exitAnimationDuration: 0,
		data: { incentive },
	};

	return dialog.open<
		JeptoAiCreditsIncentiveModalComponent,
		AiCreditsIncentiveModalData,
		boolean
	>(JeptoAiCreditsIncentiveModalComponent, config);
}
